import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { accountsAPI, dashboardAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency } from '../utils/currencyFormatter';

const Accounts = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [balanceVisible, setBalanceVisible] = useState(true);
  const [accountNumberVisible, setAccountNumberVisible] = useState(false);
  const [timeFilter, setTimeFilter] = useState('30');
  const [activeTab, setActiveTab] = useState('overview');

  // Get account holder data from authenticated user
  const accountHolder = {
    first_name: user?.first_name || user?.name?.split(' ')[0] || 'User',
    last_name: user?.last_name || user?.name?.split(' ').slice(1).join(' ') || '',
    customer_id: user?.account_number || 'FHB-2024-1001'
  };

  // Helper function to mask account number
  const maskAccountNumber = (accountNumber) => {
    if (!accountNumber) return '****0000';
    const lastFour = accountNumber.slice(-4);
    const maskedPart = '*'.repeat(Math.max(0, accountNumber.length - 4));
    return maskedPart + lastFour;
  };

  // Copy to clipboard function
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text).then(() => {
      // You could add a toast notification here
      console.log('Account number copied to clipboard');
    }).catch(err => {
      console.error('Failed to copy: ', err);
    });
  };

  // Handle quick action navigation
  const handleQuickAction = (action) => {
    if (action.path) {
      navigate(action.path);
    }
  };

  useEffect(() => {
    fetchAccounts();
  }, []);

  useEffect(() => {
    if (selectedAccount) {
      fetchTransactions(selectedAccount.id);
    }
  }, [selectedAccount, timeFilter]);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      // Use real API data instead of mock data
      const response = await dashboardAPI.getDashboard();
      
      if (response?.status === 'success') {
        const apiAccounts = response.data.accounts.map(account => ({
          id: account.id,
          account_name: account.name || account.account_name || 'Account',
          account_number: account.account_number || '',
          routing_number: account.routing_number || '021000021',
          balance: safeParseFloat(account.balance, 0),
          available_balance: safeParseFloat(account.available_balance || account.balance, 0),
          status: account.status || 'active',
          account_type: {
            code: account.type || 'CHECKING',
            name: account.type || account.name || 'Account',
            interest_rate: safeParsePercentage(getInterestRateFromAccountName(account.name || account.account_name), 0)
          },
          last_transaction_date: '2025-01-15', // Could be fetched from actual transactions
          monthly_fee: safeParseFloat(getMonthlyFeeFromAccountName(account.name || account.account_name), 0)
        }));
        
        setAccounts(apiAccounts);
        if (apiAccounts.length > 0) {
          setSelectedAccount(apiAccounts[0]);
        }
      } else {
        // Fallback to mock data if API fails
        console.warn('API failed, using fallback data');
        const mockAccounts = [
          {
            id: 1,
            account_name: 'Premium Checking',
            account_number: '1234567845',
            routing_number: '021000021',
            balance: 12547.83,
            available_balance: 12547.83,
            status: 'active',
            account_type: {
              code: 'CHECKING_PREMIUM',
              name: 'Premium Checking',
              interest_rate: 0.0125
            },
            last_transaction_date: '2025-01-15',
            monthly_fee: 0
          },
          {
            id: 2,
            account_name: 'High Yield Savings',
            account_number: '1234562391',
            routing_number: '021000021',
            balance: 8902.49,
            available_balance: 8902.49,
            status: 'active',
            account_type: {
              code: 'SAVINGS_HIGH_YIELD',
              name: 'High Yield Savings',
              interest_rate: 0.025
            },
            last_transaction_date: '2025-01-14',
            monthly_fee: 0
          },
          {
            id: 3,
            account_name: 'Investment Portfolio',
            account_number: '1234569654',
            routing_number: '021000021',
            balance: 25750.00,
            available_balance: 25750.00,
            status: 'active',
            account_type: {
              code: 'INVESTMENT',
              name: 'Investment Account',
              interest_rate: 0.08
            },
            last_transaction_date: '2025-01-13',
            monthly_fee: formatCurrencyWithUserCurrency(25)
          },
          {
            id: 4,
            account_name: 'Money Market Account',
            account_number: '1234564567',
            routing_number: '021000021',
            balance: 15000.00,
            available_balance: 15000.00,
            status: 'active',
            account_type: {
              code: 'MONEY_MARKET',
              name: 'Money Market',
              interest_rate: 0.02
            },
            last_transaction_date: '2025-01-12',
            monthly_fee: formatCurrencyWithUserCurrency(10)
          },
          {
            id: 5,
            account_name: '12-Month CD',
            account_number: '1234568901',
            routing_number: '021000021',
            balance: 10000.00,
            available_balance: 10000.00,
            status: 'active',
            account_type: {
              code: 'CD',
              name: 'Certificate of Deposit',
              interest_rate: 0.035
            },
            last_transaction_date: '2025-01-11',
            monthly_fee: 0
          }
        ];
        
        setAccounts(mockAccounts);
        if (mockAccounts.length > 0) {
          setSelectedAccount(mockAccounts[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      // Fallback to mock data on error
      const mockAccounts = [
        {
          id: 1,
          account_name: 'Premium Checking',
          account_number: '1234567845',
          routing_number: '021000021',
          balance: 12547.83,
          available_balance: 12547.83,
          status: 'active',
          account_type: {
            code: 'CHECKING_PREMIUM',
            name: 'Premium Checking',
            interest_rate: 0.0125
          },
          last_transaction_date: '2025-01-15',
          monthly_fee: 0
        },
        {
          id: 2,
          account_name: 'High Yield Savings',
          account_number: '1234562391',
          routing_number: '021000021',
          balance: 8902.49,
          available_balance: 8902.49,
          status: 'active',
          account_type: {
            code: 'SAVINGS_HIGH_YIELD',
            name: 'High Yield Savings',
            interest_rate: 0.025
          },
          last_transaction_date: '2025-01-14',
          monthly_fee: 0
        },
        {
          id: 3,
          account_name: 'Investment Portfolio',
          account_number: '1234569654',
          routing_number: '021000021',
          balance: 25750.00,
          available_balance: 25750.00,
          status: 'active',
          account_type: {
            code: 'INVESTMENT',
            name: 'Investment Account',
            interest_rate: 0.08
          },
          last_transaction_date: '2025-01-13',
                      monthly_fee: formatCurrencyWithUserCurrency(25)
        },
        {
          id: 4,
          account_name: 'Money Market Account',
          account_number: '1234564567',
          routing_number: '021000021',
          balance: 15000.00,
          available_balance: 15000.00,
          status: 'active',
          account_type: {
            code: 'MONEY_MARKET',
            name: 'Money Market',
            interest_rate: 0.02
          },
          last_transaction_date: '2025-01-12',
                      monthly_fee: formatCurrencyWithUserCurrency(10)
        },
        {
          id: 5,
          account_name: '12-Month CD',
          account_number: '1234568901',
          routing_number: '021000021',
          balance: 10000.00,
          available_balance: 10000.00,
          status: 'active',
          account_type: {
            code: 'CD',
            name: 'Certificate of Deposit',
            interest_rate: 0.035
          },
          last_transaction_date: '2025-01-11',
          monthly_fee: 0
        }
      ];
      
      setAccounts(mockAccounts);
      if (mockAccounts.length > 0) {
        setSelectedAccount(mockAccounts[0]);
      }
    } finally {
      setLoading(false);
    }
  };

  // Helper functions for account data
  const getInterestRateFromAccountName = (accountName) => {
    if (!accountName) return 0;
    const name = accountName.toLowerCase();
    if (name.includes('premium checking')) return 0.0125; // 1.25%
    if (name.includes('high yield savings')) return 0.025; // 2.5%
    if (name.includes('investment')) return 0.08; // 8.0%
    if (name.includes('money market')) return 0.02; // 2.0%
    if (name.includes('cd') || name.includes('certificate')) return 0.035; // 3.5%
    return 0.005; // Default 0.5%
  };

  const getMonthlyFeeFromAccountName = (accountName) => {
    if (!accountName) return 0;
    const name = accountName.toLowerCase();
    if (name.includes('premium checking')) return 0;
    if (name.includes('high yield savings')) return 0;
    if (name.includes('investment')) return 25;
    if (name.includes('money market')) return 10;
    if (name.includes('cd') || name.includes('certificate')) return 0;
    return 0;
  };

  const fetchTransactions = async (accountId) => {
    try {
      const response = await accountsAPI.getTransactions(accountId, { limit: 20 });
      
      if (response?.status === 'success') {
        const apiTransactions = response.data.map(transaction => ({
          id: transaction.id,
          date: transaction.transaction_date || transaction.date,
          description: transaction.description,
          amount: transaction.amount,
          type: transaction.type,
          balance: transaction.running_balance || 0,
          category: transaction.category?.name || 'Other',
          pending: transaction.status === 'pending'
        }));
        
        setTransactions(apiTransactions);
      } else {
        // Fallback to empty array if API fails
        console.warn('Failed to fetch transactions, using empty array');
        setTransactions([]);
      }
    } catch (error) {
      console.error('Error fetching transactions:', error);
      // Fallback to empty array on error
      setTransactions([]);
    }
  };

  const quickActions = [
    {
      id: 'transfer',
      name: 'Transfer',
      description: 'Move money',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
        </svg>
      ),
      color: 'blue',
      bgColor: 'bg-blue-500',
      lightBg: 'bg-blue-50',
      hoverBg: 'hover:bg-blue-100',
      path: '/transfer'
    },
    {
      id: 'pay',
      name: 'Pay Bills',
      description: 'Quick payments',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      color: 'green',
      bgColor: 'bg-green-500',
      lightBg: 'bg-green-50',
      hoverBg: 'hover:bg-green-100',
      path: '/bill-pay'
    },
    {
      id: 'deposit',
      name: 'Deposit',
      description: 'Mobile check',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      color: 'purple',
      bgColor: 'bg-purple-500',
      lightBg: 'bg-purple-50',
      hoverBg: 'hover:bg-purple-100',
      path: '/deposit'
    },
    {
      id: 'zelle',
      name: 'Send Money',
      description: 'Zelle transfer',
      icon: (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      ),
      color: 'orange',
      bgColor: 'bg-orange-500',
      lightBg: 'bg-orange-50',
      hoverBg: 'hover:bg-orange-100',
      path: '/zelle'
    }
  ];

  const getAccountIcon = (accountType) => {
    const iconProps = "w-5 h-5";
    switch (accountType?.code) {
      case 'CHECKING_PREMIUM':
        return (
          <svg className={iconProps} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
          </svg>
        );
      case 'SAVINGS_HIGH_YIELD':
        return (
          <svg className={iconProps} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-6m-4 0H3m2-16h14" />
          </svg>
        );
      case 'INVESTMENT':
        return (
          <svg className={iconProps} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
          </svg>
        );
      default:
        return (
          <svg className={iconProps} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        );
    }
  };

  const getAccountIconColor = (accountType) => {
    switch (accountType?.code) {
      case 'CHECKING_PREMIUM':
        return { bg: 'bg-blue-500', text: 'text-white' };
      case 'SAVINGS_HIGH_YIELD':
        return { bg: 'bg-green-500', text: 'text-white' };
      case 'INVESTMENT':
        return { bg: 'bg-purple-500', text: 'text-white' };
      default:
        return { bg: 'bg-gray-500', text: 'text-white' };
    }
  };

  const formatCurrencyWithUserCurrency = (amount) => {
    return formatCurrency(amount, user?.currency_code || 'USD');
  };

  const getTotalBalance = () => {
    return accounts.reduce((total, account) => {
      const balance = parseFloat(account.balance) || 0;
      return total + balance;
    }, 0);
  };

  // Helper function to safely parse numeric values
  const safeParseFloat = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === '') return defaultValue;
    const parsed = parseFloat(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  // Helper function to safely parse percentage values
  const safeParsePercentage = (value, defaultValue = 0) => {
    const parsed = safeParseFloat(value, defaultValue);
    return parsed > 1 ? parsed / 100 : parsed; // Convert if it's in percentage format (e.g., 2.5 vs 0.025)
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex-1">
              <h1 className="text-xl font-semibold text-gray-900 text-left">
                {accountHolder.first_name} {accountHolder.last_name}
              </h1>
              <div className="flex items-center space-x-2 mt-1">
                <p className="text-sm text-gray-500">
                  {accountNumberVisible 
                    ? (accounts.length > 0 ? accounts[0].account_number : user?.account_number || '****0000')
                    : (accounts.length > 0 ? maskAccountNumber(accounts[0].account_number) : maskAccountNumber(user?.account_number || '****0000'))
                  }
                </p>
                <button
                  onClick={() => copyToClipboard(accounts.length > 0 ? accounts[0].account_number : user?.account_number || '')}
                  className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                  title="Copy account number"
                >
                  <svg className="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                </button>
                <button
                  onClick={() => setAccountNumberVisible(!accountNumberVisible)}
                  className="p-1 rounded-full hover:bg-gray-100 transition-colors"
                  title={accountNumberVisible ? "Hide account number" : "Show account number"}
                >
                  {accountNumberVisible ? (
                    <svg className="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  )}
                </button>
              </div>
            </div>
            <div className="flex items-center space-x-3">
              <div className="text-right mr-4">
                <p className="text-sm font-medium text-gray-900">
                  {balanceVisible ? formatCurrencyWithUserCurrency(getTotalBalance()) : '••••••••'}
                </p>
                <p className="text-xs text-gray-500">Total Balance</p>
              </div>
              <button
                onClick={() => setBalanceVisible(!balanceVisible)}
                className="p-2 rounded-lg bg-blue-50 hover:bg-blue-100 transition-colors"
                title={balanceVisible ? "Hide balance" : "Show balance"}
              >
                {balanceVisible ? (
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-6">
        {/* Portfolio Summary */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">Total Portfolio</h2>
            <span className="text-sm text-gray-500">All accounts</span>
          </div>
          
          <div className="mb-6">
            <div className="text-3xl font-bold text-gray-900 mb-1">
                                {balanceVisible ? formatCurrencyWithUserCurrency(getTotalBalance()) : '••••••••'}
            </div>
            <div className="flex items-center text-green-600">
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              <span className="text-sm font-medium">+3.2% this month</span>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {quickActions.map((action, index) => (
              <motion.button
                key={action.id}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                onClick={() => handleQuickAction(action)}
                className={`flex flex-col items-center p-4 rounded-xl ${action.lightBg} ${action.hoverBg} transition-colors group`}
              >
                <div className={`w-12 h-12 rounded-full ${action.bgColor} shadow-sm flex items-center justify-center mb-3 group-hover:shadow-md transition-shadow`}>
                  <div className="text-white">
                    {action.icon}
                  </div>
                </div>
                <p className="font-medium text-gray-900 text-sm text-center leading-tight">{action.name}</p>
                <p className="text-gray-500 text-xs text-center mt-1">{action.description}</p>
              </motion.button>
            ))}
          </div>
        </motion.div>

        {/* Accounts Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Accounts List */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-1"
          >
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200">
              <div className="p-6 border-b border-gray-200">
                <h3 className="text-lg font-semibold text-gray-900">Your Accounts</h3>
              </div>
              
              <div className="p-4 space-y-3 max-h-96 overflow-y-auto">
                {accounts.map((account, index) => {
                  const iconColors = getAccountIconColor(account.account_type);
                  return (
                    <motion.div
                      key={account.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.4, delay: 0.4 + index * 0.1 }}
                      onClick={() => setSelectedAccount(account)}
                      className={`p-4 rounded-xl cursor-pointer transition-all duration-200 border-2 ${
                        selectedAccount?.id === account.id
                          ? 'border-gray-900 bg-gray-50'
                          : 'border-gray-100 hover:border-gray-200 bg-white'
                      }`}
                      whileHover={{ scale: 1.01 }}
                      whileTap={{ scale: 0.99 }}
                    >
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center space-x-3">
                          <div className={`p-2 rounded-lg ${
                            selectedAccount?.id === account.id ? 'bg-gray-900 text-white' : `${iconColors.bg} ${iconColors.text}`
                          }`}>
                            {getAccountIcon(account.account_type)}
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900 text-sm">{account.account_name}</h4>
                            <p className="text-xs text-gray-500">
                              {accountNumberVisible ? account.account_number : maskAccountNumber(account.account_number)}
                            </p>
                          </div>
                        </div>
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Active
                        </span>
                      </div>
                      
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-lg font-bold text-gray-900">
                            {balanceVisible ? formatCurrencyWithUserCurrency(account.balance) : '••••••'}
                          </p>
                          <p className="text-xs text-gray-500">Available</p>
                        </div>
                        {safeParseFloat(account.account_type.interest_rate, 0) > 0 && (
                          <div className="text-right">
                            <p className="text-green-600 font-semibold text-sm">
                              {(safeParseFloat(account.account_type.interest_rate, 0) * 100).toFixed(2)}%
                            </p>
                            <p className="text-xs text-gray-500">APY</p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </motion.div>

          {/* Account Details and Transactions */}
          <div className="lg:col-span-2 space-y-6">
            {selectedAccount ? (
              <>
                {/* Account Details */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.4 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200"
                >
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900">{selectedAccount.account_name}</h3>
                      <div className="flex space-x-2">
                        <button className="px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                          Statement
                        </button>
                        <button className="px-4 py-2 text-sm bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors">
                          Settings
                        </button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                      <div className="text-center lg:text-left">
                        <div className="flex items-center justify-center lg:justify-start space-x-2">
                          <div>
                            <p className="text-xs text-gray-500 mb-1">Account Number</p>
                            <p className="font-semibold text-gray-900">
                              {accountNumberVisible ? selectedAccount.account_number : maskAccountNumber(selectedAccount.account_number)}
                            </p>
                          </div>
                          {accountNumberVisible && (
                            <button
                              onClick={() => copyToClipboard(selectedAccount.account_number)}
                              className="p-1 rounded-full hover:bg-gray-100 transition-colors mt-4"
                              title="Copy account number"
                            >
                              <svg className="w-3 h-3 text-gray-400 hover:text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                              </svg>
                            </button>
                          )}
                        </div>
                      </div>
                      <div className="text-center lg:text-left">
                        <p className="text-xs text-gray-500 mb-1">Routing Number</p>
                        <p className="font-semibold text-gray-900">{selectedAccount.routing_number}</p>
                      </div>
                      <div className="text-center lg:text-left">
                        <p className="text-xs text-gray-500 mb-1">Interest Rate</p>
                        <p className="font-semibold text-green-600">
                          {(safeParseFloat(selectedAccount.account_type.interest_rate, 0) * 100).toFixed(2)}% APY
                        </p>
                      </div>
                      <div className="text-center lg:text-left">
                        <p className="text-xs text-gray-500 mb-1">Monthly Fee</p>
                        <p className="font-semibold text-gray-900">
                          {safeParseFloat(selectedAccount.monthly_fee, 0) === 0 ? 'FREE' : formatCurrencyWithUserCurrency(selectedAccount.monthly_fee)}
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>

                {/* Transactions */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.6, delay: 0.6 }}
                  className="bg-white rounded-2xl shadow-sm border border-gray-200"
                >
                  <div className="p-6 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <h3 className="text-lg font-semibold text-gray-900">Recent Transactions</h3>
                      <select
                        value={timeFilter}
                        onChange={(e) => setTimeFilter(e.target.value)}
                        className="px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="7">Last 7 days</option>
                        <option value="30">Last 30 days</option>
                        <option value="90">Last 90 days</option>
                        <option value="365">Last year</option>
                      </select>
                    </div>
                  </div>

                  <div className="p-6">
                    <div className="space-y-4">
                      {transactions.map((transaction, index) => (
                        <motion.div
                          key={transaction.id}
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          transition={{ duration: 0.4, delay: 0.8 + index * 0.1 }}
                          className="flex items-center justify-between p-4 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
                        >
                          <div className="flex items-center space-x-4">
                            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                              transaction.type === 'credit' 
                                ? 'bg-green-100 text-green-600' 
                                : 'bg-red-100 text-red-600'
                            }`}>
                              {transaction.type === 'credit' ? (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                </svg>
                              ) : (
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
                                </svg>
                              )}
                            </div>
                            <div>
                              <h4 className="font-medium text-gray-900">{transaction.description}</h4>
                              <div className="flex items-center gap-2 mt-1">
                                <p className="text-sm text-gray-500">{transaction.date}</p>
                                <span className="text-gray-300">•</span>
                                <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                                  transaction.category === 'Income' ? 'bg-green-100 text-green-800' :
                                  transaction.category === 'Transfer' ? 'bg-blue-100 text-blue-800' :
                                  transaction.category === 'Shopping' ? 'bg-purple-100 text-purple-800' :
                                  transaction.category === 'Cash' ? 'bg-orange-100 text-orange-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {transaction.category}
                                </span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className={`font-semibold ${
                              transaction.type === 'credit' ? 'text-green-600' : 'text-red-600'
                            }`}>
                              {transaction.type === 'credit' ? '+' : '-'}{formatCurrencyWithUserCurrency(transaction.amount)}
                            </p>
                            <p className="text-sm text-gray-500">
                              {balanceVisible ? formatCurrencyWithUserCurrency(transaction.balance) : '••••••'}
                            </p>
                          </div>
                        </motion.div>
                      ))}
                    </div>

                    <div className="mt-6 text-center">
                      <button className="px-6 py-2 text-sm text-blue-600 border border-blue-600 rounded-lg hover:bg-blue-50 transition-colors">
                        View All Transactions
                      </button>
                    </div>
                  </div>
                </motion.div>
              </>
            ) : (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-12 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-6m-4 0H3m2-16h14" />
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Select an Account</h3>
                <p className="text-gray-500">Choose an account to view details and transactions</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Bottom Navigation */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 lg:hidden">
        <div className="grid grid-cols-4 h-16">
          <button
            onClick={() => setActiveTab('overview')}
            className={`flex flex-col items-center justify-center space-y-1 transition-colors ${
              activeTab === 'overview' ? 'text-blue-600' : 'text-gray-500'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
            </svg>
            <span className="text-xs">Overview</span>
          </button>
          
          <button
            onClick={() => setActiveTab('transfer')}
            className={`flex flex-col items-center justify-center space-y-1 transition-colors ${
              activeTab === 'transfer' ? 'text-green-600' : 'text-gray-500'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
            </svg>
            <span className="text-xs">Transfer</span>
          </button>
          
          <button
            onClick={() => setActiveTab('pay')}
            className={`flex flex-col items-center justify-center space-y-1 transition-colors ${
              activeTab === 'pay' ? 'text-purple-600' : 'text-gray-500'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <span className="text-xs">Pay</span>
          </button>
          
          <button
            onClick={() => setActiveTab('more')}
            className={`flex flex-col items-center justify-center space-y-1 transition-colors ${
              activeTab === 'more' ? 'text-orange-600' : 'text-gray-500'
            }`}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className="text-xs">More</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default Accounts; 